/**
 * Mapbox GL — lazy-loaded initialiser for `[data-mapbox]` canvases rendered
 * by template-parts/blocks/block-map.php.
 *
 * Only zoom/compass navigation is shown; Mapbox logo and attribution are
 * suppressed in config and also by CSS (see src/sass/85_blocks.sass).
 *
 * Reads the access token from the localized `php_vars.mapboxToken` (see
 * library/enqueue-scripts.php → MAPBOX_ACCESS_TOKEN in .env).
 */

const MAPBOX_VERSION = '3.9.0';
const CDN_JS  = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js`;
const CDN_CSS = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css`;

let libPromise = null;

const loadMapboxLib = () => {
    if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
    if (libPromise) return libPromise;

    if (!document.querySelector('link[data-mapbox-css]')) {
        const css = document.createElement('link');
        css.rel = 'stylesheet';
        css.href = CDN_CSS;
        css.dataset.mapboxCss = '';
        document.head.appendChild(css);
    }

    libPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = CDN_JS;
        script.async = true;
        script.onload = () => resolve(window.mapboxgl);
        script.onerror = reject;
        document.head.appendChild(script);
    });

    return libPromise;
};

const EARTH_ZOOM = 0.5;
const FLY_DURATION = 3000;
const FLY_DELAY = 1200;

const initMap = (mapboxgl, container) => {
    const lat         = parseFloat(container.dataset.lat);
    const lng         = parseFloat(container.dataset.lng);
    const zoom        = parseInt(container.dataset.zoom, 10) || 15;
    const style       = 'mapbox://styles/glunz/cmobfi2fg002301peelxmhx9v';
    const markerTitle = container.dataset.markerTitle || '';
    const mapsUrl     = container.dataset.mapsUrl || '';
    const markerIcon  = container.dataset.markerIcon || '';

    if (Number.isNaN(lat) || Number.isNaN(lng)) {
        console.warn('[mapbox] invalid coordinates on', container);
        return;
    }

    console.log('[mapbox] init', { lat, lng, zoom, style, token: mapboxgl.accessToken?.slice(0, 12) + '…' });

    const map = new mapboxgl.Map({
        container,
        style,
        center: [lng, lat],
        zoom: EARTH_ZOOM,
        attributionControl: false,      // hide "© Mapbox © OpenStreetMap"
        logoPosition: 'bottom-left',    // still rendered by lib; CSS hides it
        cooperativeGestures: false,
        scrollZoom: false,              // don't hijack the page scroll
    });

    // --- debugging: surface style / token / network errors loudly ---
    map.on('error', (e) => {
        const err = e?.error || e;
        console.error('[mapbox] map error:', err?.message || err, err);
    });
    map.on('style.load', () => {
        const s = map.getStyle();
        console.log('[mapbox] style.load OK —', s?.name || style);
    });
    map.on('styledataloading', () => console.log('[mapbox] styledata loading…', style));
    // ----------------------------------------------------------------

    // Zoom-only control. No compass, no fullscreen, no geolocate, no scale.
    map.addControl(new mapboxgl.NavigationControl({
        showCompass: false,
        showZoom: true,
        visualizePitch: false,
    }), 'top-right');

    let markerEl;
    if (markerIcon) {
        markerEl = document.createElement('div');
        markerEl.className = 'block-map-marker';
        const img = document.createElement('img');
        img.src = markerIcon;
        img.alt = '';
        img.draggable = false;
        markerEl.appendChild(img);
    }

    const marker = new mapboxgl.Marker(markerEl ? { element: markerEl, anchor: 'bottom' } : undefined)
        .setLngLat([lng, lat])
        .addTo(map);

    markerEl = marker.getElement();
    if (markerTitle) {
        markerEl.setAttribute('aria-label', markerTitle);
        markerEl.setAttribute('title', markerTitle);
    }

    if (mapsUrl) {
        markerEl.style.cursor = 'pointer';
        markerEl.setAttribute('role', 'link');
        markerEl.setAttribute('tabindex', '0');
        const openMaps = (e) => {
            e?.preventDefault?.();
            e?.stopPropagation?.();
            window.open(mapsUrl, '_blank', 'noopener,noreferrer');
        };
        markerEl.addEventListener('click', openMaps);
        markerEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') openMaps(e);
        });
    }

    container.__mapbox = map;

    let flown = false;
    let styleReady = false;
    let inView = false;

    const maybeFly = () => {
        if (flown || !styleReady || !inView) return;
        flown = true;
        setTimeout(() => {
            map.flyTo({
                center: [lng, lat],
                zoom,
                duration: FLY_DURATION,
                essential: true,
                curve: 1.6,
                easing: (t) => t * t * t,
            });
        }, FLY_DELAY);
    };

    map.on('style.load', () => {
        styleReady = true;
        maybeFly();
    });

    if (typeof IntersectionObserver === 'function') {
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    inView = true;
                    observer.disconnect();
                    maybeFly();
                    break;
                }
            }
        }, { threshold: 0.3 });
        observer.observe(container);
    } else {
        inView = true;
        maybeFly();
    }
};

const init = async () => {
    const containers = document.querySelectorAll('[data-mapbox]');
    if (containers.length === 0) return;

    const token = (window.php_vars && window.php_vars.mapboxToken) || '';
    if (!token) {
        console.warn('[mapbox] MAPBOX_ACCESS_TOKEN is empty — set it in .env');
        return;
    }

    try {
        const mapboxgl = await loadMapboxLib();
        mapboxgl.accessToken = token;
        containers.forEach((container) => initMap(mapboxgl, container));
    } catch (err) {
        console.error('[mapbox] failed to load library', err);
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
